import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export async function fetchOperationalDashboardV2(token: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_OPERATIONAL_DASHBOARD_V2}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchReadinessWithActions(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_READINESS_ACTIONS(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function executeBlockerAction(
  token: string,
  systemId: string,
  blockerCode: string,
  orgId?: string | null,
  body?: Record<string, unknown>
) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_BLOCKER_ACTION(systemId, blockerCode)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(body || {}) }
  )
}

export async function fetchEvidenceOperations(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_EVIDENCE_OPERATIONS(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchSlaStatus(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_SLA_STATUS(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchComplianceImpact(
  token: string,
  systemId: string,
  orgId?: string | null,
  params?: { controlLinkId?: string; standardCode?: string; controlId?: string }
) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_COMPLIANCE_IMPACT(systemId)}${orgQuery(orgId, params as Record<string, string>)}`,
    token
  )
}

export async function fetchRemediationContext(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<unknown[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_REMEDIATION_CONTEXT(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function updateExposureLifecycle(
  token: string,
  systemId: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_EXPOSURE_LIFECYCLE(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'PUT', body: JSON.stringify(payload) }
  )
}

export async function fetchBlastRadius(
  token: string,
  kind: 'vulnerabilities' | 'controls' | 'evidence' | 'remediation',
  orgId?: string | null,
  params?: Record<string, string>
) {
  const url =
    kind === 'vulnerabilities'
      ? API_ENDPOINTS.ASSETS_BLAST_RADIUS_VULNERABILITIES
      : kind === 'controls'
        ? API_ENDPOINTS.ASSETS_BLAST_RADIUS_CONTROLS
        : kind === 'evidence'
          ? API_ENDPOINTS.ASSETS_BLAST_RADIUS_EVIDENCE
          : API_ENDPOINTS.ASSETS_BLAST_RADIUS_REMEDIATION
  return apiFetch<Record<string, unknown>>(`${url}${orgQuery(orgId, params)}`, token)
}

export async function fetchOperationalAlerts(token: string, orgId?: string | null) {
  return apiFetch<unknown[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_OPERATIONAL_ALERTS}${orgQuery(orgId)}`,
    token
  )
}
